import {useCallback} from 'react';
import {TWhatsAppProps, TEmailProps, TSmsProps} from '../types';
import {TXHR} from '../../../assets/constants/constansts.type';
import {TemplateStructure, TextTemplateItem} from '../../whatsAppTemplateEditor/types/templateStructure';
import {SendData, ChannelType, MailingSource} from '../../../hooks/sendHooks.types';
import {serializeTemplateStructure} from '../../whatsAppTemplateEditor/utils/formatUtils';
import store from '../../../store';

interface CreateSendingPropsParams {
    whatsappData: {image: {path: string, imageId: string}, message: string, whatsappTemplate?: TemplateStructure};
    emailData: {image: {path: string, imageId: string}, emailBody: string, emailSubject: string};
    smsData: {message: string};
    handleDataSave: (data: unknown, key: string, type: keyof SendData) => void;
    handleSend: (type: ChannelType, trialRecipients?: string[]) => Promise<boolean>;
    handleSave?: (type: ChannelType) => Promise<boolean>;
    sendingXHR: TXHR;
    whatsappTemplate: TemplateStructure;
    variablesType: string;
    mailingSource?: MailingSource;
    emailButtons?: React.ReactNode;
}

export const createSendingProps = ({
    whatsappData,
    emailData,
    smsData,
    handleDataSave,
    handleSend,
    handleSave,
    sendingXHR,
    whatsappTemplate,
    variablesType,
    mailingSource,
    emailButtons
}: CreateSendingPropsParams) => {
    const getWhatsappEditorMode = () => {
        const modeKey = mailingSource ?? variablesType ?? 'default';
        return store.getState().ui?.whatsappEditorMode?.[modeKey];
    };

    const createProps = useCallback((type: 'whatsapp' | 'email' | 'sms') => {
        const baseProps = {
            send: () => handleSend(type),
            save: handleSave ? () => handleSave(type) : undefined,
            sendTrial: (trialPhones: string[]) => handleSend(type, trialPhones),
            sendingXHR
        };

        switch (type) {
            case 'whatsapp':
                return {
                    ...baseProps,
                    image: whatsappData.image,
                    message: whatsappData.message ?? '',
                    whatsappTemplate: whatsappData.whatsappTemplate || whatsappTemplate,
                    saveImage: (image: string) => handleDataSave({path: image, imageId: ''}, 'image', type),
                    saveText: (payload: TemplateStructure | TextTemplateItem[] | string) => {
                        const editorMode = getWhatsappEditorMode();
                        const payloadAsString = typeof payload === 'string' ? payload : serializeTemplateStructure(payload);
                        if(editorMode === 'template' && Array.isArray(payload)) {
                            handleDataSave(payload, 'whatsappTemplate', type);
                        }
                        handleDataSave(payloadAsString, 'message', type);
                    },
                    variablesType,
                    ...(mailingSource != null && {mailingSource})
                } as TWhatsAppProps;

            case 'email':
                return {
                    ...baseProps,
                    image: emailData.image,
                    emailBody: emailData.emailBody,
                    emailSubject: emailData.emailSubject,
                    setEmailBody: (text: string) => handleDataSave(text, 'emailBody', type),
                    setEmailSubject: (subject: string) => handleDataSave(subject, 'emailSubject', type),
                    saveImage: (image: string) => handleDataSave({path: image, imageId: ''}, 'image', type),
                    variablesType,
                    buttons: emailButtons
                } as TEmailProps;

            case 'sms':
                return {
                    ...baseProps,
                    message: smsData.message,
                    saveMessage: (message: string) => handleDataSave(message, 'message', type),
                    variablesType
                } as TSmsProps;

            default:
                return baseProps;
        }
    }, [whatsappData, emailData, smsData, handleDataSave, handleSend, handleSave, sendingXHR, whatsappTemplate, variablesType, mailingSource, emailButtons]);

    return {
        whatsappProps: createProps('whatsapp') as TWhatsAppProps,
        emailProps: createProps('email') as TEmailProps,
        smsProps: createProps('sms') as TSmsProps
    };
};
